import { useLayoutEffect, useRef } from 'react';
import Box from '@mui/material/Box';
import { alpha } from '@mui/material/styles';
import type { FreshRange } from './refEditor/FreshTextDecoration';

/** What the overlay copies from the textarea so its lines break the same. */
const COPIED_STYLES = [
    'fontFamily',
    'fontSize',
    'fontWeight',
    'fontStyle',
    'letterSpacing',
    'lineHeight',
    'textTransform',
    'wordSpacing',
    'tabSize',
    'paddingTop',
    'paddingRight',
    'paddingBottom',
    'paddingLeft'
] as const;

/**
 * Tints a stretch of a plain textarea's text — what the Befund-chip editor
 * does with a decoration (see FreshTextDecoration), for the fields without
 * one. A textarea cannot colour part of its text, so this lays a copy of the
 * text over it, in the same font and box, with the letters transparent and
 * only the stretch's background showing. It takes no pointer events: clicks
 * and the caret stay the textarea's.
 *
 * Mounted as the InputBase's adornment, inside its (positioned) root and next
 * to the textarea, which it finds there and measures. The measuring writes
 * straight to the overlay's style: it follows the textarea's box, which React
 * does not know about.
 */
export default function TextareaHighlight({
    value,
    range
}: {
    value: string;
    range: FreshRange | null;
}) {
    const frameRef = useRef<HTMLDivElement | null>(null);
    const textRef = useRef<HTMLDivElement | null>(null);
    const visible = !!range && range.end > range.start;

    useLayoutEffect(() => {
        const frame = frameRef.current;
        const copy = textRef.current;
        const textarea = frame?.parentElement?.querySelector('textarea');
        if (!visible || !frame || !copy || !textarea) {
            return;
        }
        const follow = () => {
            frame.style.top = `${textarea.offsetTop}px`;
            frame.style.left = `${textarea.offsetLeft}px`;
            frame.style.width = `${textarea.clientWidth}px`;
            frame.style.height = `${textarea.clientHeight}px`;
            const computed = window.getComputedStyle(textarea);
            for (const key of COPIED_STYLES) {
                copy.style[key] = computed[key];
            }
            copy.style.transform = `translateY(${-textarea.scrollTop}px)`;
        };
        follow();
        const observer = new ResizeObserver(follow);
        observer.observe(textarea);
        textarea.addEventListener('scroll', follow);
        return () => {
            observer.disconnect();
            textarea.removeEventListener('scroll', follow);
        };
    }, [visible, value]);

    if (!visible) {
        return null;
    }
    return (
        <Box
            ref={frameRef}
            aria-hidden
            sx={{
                position: 'absolute',
                overflow: 'hidden',
                pointerEvents: 'none'
            }}
        >
            <Box
                ref={textRef}
                sx={(theme) => ({
                    m: 0,
                    color: 'transparent',
                    whiteSpace: 'pre-wrap',
                    overflowWrap: 'break-word',
                    wordBreak: 'break-word',
                    '& mark': {
                        color: 'transparent',
                        backgroundColor: alpha(theme.palette.info.main, 0.22),
                        borderRadius: '2px'
                    }
                })}
            >
                {value.slice(0, range.start)}
                <mark>{value.slice(range.start, range.end)}</mark>
                {/* A trailing newline would otherwise collapse the last line. */}
                {`${value.slice(range.end)} `}
            </Box>
        </Box>
    );
}
